import { create } from 'zustand';

export interface Accomplice {
  id: string;
  code: string;
  name?: string;
  threatLevel: 'High' | 'Medium' | 'Low';
  photoUrl: string;
  notes?: string; // Translation key
}

export interface Vehicle {
  licensePlate: string;
  make?: string;
  model?: string;
  color?: string;
  location?: string; // Translation key
  photoUrl?: string;
}

export interface RecentFace {
  id: string;
  imageUrl: string;
  timestamp: Date;
  assignedCode?: string;
  sex?: 'M' | 'F' | 'V'; 
}

export interface POIEditHistory {
  editedBy: string;
  editedByName?: string | null;
  editedAt: string | Date;
  field: string;
  oldValue: any;
  newValue: any;
}

export interface POI {
  id: string;
  code: string;
  name?: string;
  threatLevel: 'High' | 'Medium' | 'Low';
  photoUrl: string;
  lastSeen?: string | Date;
  notes: string; 
  accomplices?: Accomplice[];
  vehicle?: Vehicle | null;
  createdBy?: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  editHistory?: POIEditHistory[];
  storeId?: string;       
  storeName?: string | null;
  isNetwork?: boolean;
  storeClientId?: string | null;
  sex?: 'F' | 'M' | 'V';
}

export interface Alert {
  id: string;
  timestamp: Date;
  poi?: POI | null;
  acknowledgedBy?: string;
  capture_image_url?: string;   // for AC demo alerts
}

interface DemoState {
  isDemoMode: boolean;
  toggleDemoMode: () => void;
  activeAlerts: Alert[];
  triggerAlert: (poi: POI) => void;
  dismissAlert: (alertId: string) => void;
  clearAllAlerts: () => void;
  mockPOIs: POI[];
  recentFaces: RecentFace[];
  addRecentFace: (face: RecentFace) => void;
  flagFaceAsPOI: (faceId: string, details: Partial<POI>, guardName: string) => void;
  updatePOI: (poiId: string, updates: Partial<POI>, editorName: string) => void;
  //acknowledgedAlertIds: string[];
  //unacknowledgeAlert: (alertId: string) => void;
  acknowledgeAlert: (alertId: string, guardName: string) => void;
}

// Mock accomplices for Carlos M. – using translation keys
const carlosAccomplices: Accomplice[] = [
  {
    id: 'a1',
    code: 'ES-MAD-00987-F-2',
    threatLevel: 'Medium',
    photoUrl: 'https://randomuser.me/api/portraits/women/28.jpg',
    notes: 'guard.accomplice.lookout.notes',
  },
  {
    id: 'a2',
    code: 'ES-MAD-00543-M-2',
    threatLevel: 'Medium',
    photoUrl: 'https://randomuser.me/api/portraits/men/45.jpg',
    notes: 'guard.accomplice.distraction.notes',
  },
  {
    id: 'a3',
    code: 'ES-MAD-00234-F-3',
    threatLevel: 'High',
    photoUrl: 'https://randomuser.me/api/portraits/women/62.jpg',
    notes: 'guard.accomplice.driver.notes',
  },
];

const mockPOIs: POI[] = [
  {
    id: '1',
    code: 'ES-MAD-00123-M-3',
    threatLevel: 'High',
    photoUrl: 'https://randomuser.me/api/portraits/men/32.jpg',
    lastSeen: new Date('2026-03-15'),
    notes: 'demo.poi.carlos.notes',
    accomplices: carlosAccomplices,
    vehicle: {
      licensePlate: '1234 KMN',
      make: 'Toyota',
      model: 'Camry',
      color: 'Silver',
      location: 'guard.vehicle.parking.location',
      photoUrl: 'https://a.drive-data.ru/e245185s-1920.jpg',
    },
    editHistory: [
      {
        editedBy: 'Admin',
        editedAt: new Date('2025-01-10'),
        field: 'threatLevel',
        oldValue: 'Low',
        newValue: 'High',
      },
    ],
  },
  {
    id: '2',
    code: 'ES-BCN-00456-F-1',
    threatLevel: 'Medium',
    photoUrl: 'https://randomuser.me/api/portraits/women/44.jpg',
    lastSeen: new Date('2026-04-01'),
    notes: 'demo.poi.fraud.notes',
    accomplices: [],
    vehicle: undefined,
    editHistory: [
          {
            editedBy: 'Admin',
            editedAt: new Date('2025-01-10'),
            field: 'threatLevel',
            oldValue: 'Low',
            newValue: 'High',
          },
    ],
  },
  {
    id: '3',
    code: 'FR-PAR-00789-M-2',
    threatLevel: 'Low',
    photoUrl: 'https://randomuser.me/api/portraits/men/68.jpg',
    lastSeen: new Date('2026-02-20'),
    notes: 'demo.poi.trespass.notes',
    accomplices: [],
    vehicle: undefined,
    editHistory: [
              {
                editedBy: 'Admin',
                editedAt: new Date('2025-01-10'),
                field: 'threatLevel',
                oldValue: 'Low',
                newValue: 'High',
              },
    ],
  },
];

/*
// Generate mock faces (simulates edge device captures)
const generateMockFaces = (): RecentFace[] => {
  const faces = [
    'https://randomuser.me/api/portraits/men/1.jpg',
    'https://randomuser.me/api/portraits/women/2.jpg',
    'https://randomuser.me/api/portraits/men/3.jpg',
    'https://randomuser.me/api/portraits/women/4.jpg',
  ];
  return faces.map((url, i) => ({
    id: `face-${i}`,
    imageUrl: url,
    timestamp: new Date(Date.now() - Math.random() * 72 * 3600000), // within last 72h
    assignedCode: `TEMP-${Math.floor(Math.random() * 10000)}`,
  }));
};
*/

const generateMockFaces = (): RecentFace[] => {
  const faces: RecentFace[] = [];
  const baseTime = Date.now();
  const sexes: Array<'M' | 'F' | 'V'> = ['M', 'F', 'V'];
  const images = {
    M: [1,3,5,7,9,11,12,13,15].map(n => `https://randomuser.me/api/portraits/men/${n}.jpg`),
    F: [2,4,6,8,10,14,16,18,20].map(n => `https://randomuser.me/api/portraits/women/${n}.jpg`),
    V: [
      'https://a.drive-data.ru/e245185s-1920.jpg',
      'https://a.drive-data.ru/e245185s-1920.jpg', // reuse for demo
    ],
  };

  for (let i = 0; i < 20; i++) {
    const sex = sexes[Math.floor(Math.random() * sexes.length)];
    const imgList = images[sex];
    const imageUrl = imgList[Math.floor(Math.random() * imgList.length)];
    const offset = Math.random() * 72 * 3600000; // up to 72h ago
    faces.push({
      id: `face-${i}`,
      imageUrl,
      timestamp: new Date(baseTime - offset),
      sex,
    });
  }

  return faces;
};

// Helper to generate unique IDs
const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Date.now() + '-' + Math.random().toString(36).substr(2, 9);
};

export const useDemoStore = create<DemoState>((set) => ({
  isDemoMode: true,
  // toggleDemoMode: () => set((state) => ({ isDemoMode: !state.isDemoMode })),
  toggleDemoMode: () => {}, // no-op

  activeAlerts: [],
  triggerAlert: (poi) =>
    set((state) => {
      // Ignore if this POI already has an active alert
      const alreadyActive = state.activeAlerts.some(a => a.poi && a.poi.id === poi.id);
      if (alreadyActive) return state; // do nothing
      return {
        activeAlerts: [
          ...state.activeAlerts,
          { id: generateId(), timestamp: new Date(), poi },
        ],
      };
    }),
  triggerAccessAlert: (imageUrl: string) =>
  set((state) => ({
    activeAlerts: [
      ...state.activeAlerts,
      {
        id: generateId(),
        timestamp: new Date(),
        capture_image_url: imageUrl,
        // no poi
      },
    ],
  })),
  dismissAlert: (alertId) =>
    set((state) => ({
      activeAlerts: state.activeAlerts.filter((a) => a.id !== alertId),
    })),
  clearAllAlerts: () => set({ activeAlerts: [] }),

  mockPOIs,

  recentFaces: generateMockFaces(),
  addRecentFace: (face) =>
    set((state) => ({
      recentFaces: [face, ...state.recentFaces].slice(0, 50),
    })),

  flagFaceAsPOI: (faceId, details, guardName) =>
    set((state) => {
      const face = state.recentFaces.find((f) => f.id === faceId);
      if (!face) return state;

      const newPOI: POI = {
        id: generateId(),
        code: details.code || `POI-${Date.now()}`,
        threatLevel: details.threatLevel || 'Medium',
        photoUrl: face.imageUrl,
        notes: details.notes || '',
        createdBy: guardName,
        createdAt: new Date(),
        editHistory: [],
        accomplices: details.accomplices || [],
        vehicle: details.vehicle,
        lastSeen: new Date(),
      };

      const filteredFaces = state.recentFaces.filter((f) => f.id !== faceId);

      return {
        mockPOIs: [newPOI, ...state.mockPOIs],
        recentFaces: filteredFaces,
      };
    }),

  updatePOI: (poiId, updates, editorName) =>
    set((state) => ({
      mockPOIs: state.mockPOIs.map((poi) => {
        if (poi.id !== poiId) return poi;
        const oldValue = { ...poi };
        const newValue = { ...poi, ...updates };
        const historyEntry: POIEditHistory = {
          editedBy: editorName,
          editedAt: new Date(),
          field: Object.keys(updates).join(', '),
          oldValue,
          newValue,
        };
        const newHistory = [...(poi.editHistory || []), historyEntry];
        return { ...newValue, editHistory: newHistory };
      }),
    })),

  // Removing logic to add shield new logic
  /*
  acknowledgedAlertIds: [],
    acknowledgeAlert: (alertId) =>
      set((state) => ({
        acknowledgedAlertIds: [...state.acknowledgedAlertIds, alertId],
      })),
    unacknowledgeAlert: (alertId) =>
      set((state) => ({
        acknowledgedAlertIds: state.acknowledgedAlertIds.filter((id) => id !== alertId),
    })),
  */

  // inside the store
  acknowledgeAlert: (alertId, guardName) =>
    set((state) => ({
      activeAlerts: state.activeAlerts.map((a) =>
        a.id === alertId ? { ...a, acknowledgedBy: guardName } : a
      ),
    })),

}));
